const STORAGE_KEY = 'api-password';

export interface ApiPasswordState {
  password: string | null;
  isRejected: boolean;
}

type Listener = () => void;

const listeners = new Set<Listener>();

const readStoredPassword = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

let snapshot: ApiPasswordState = {
  password: readStoredPassword(),
  isRejected: false,
};

const setSnapshot = (next: ApiPasswordState) => {
  snapshot = next;
  listeners.forEach((listener) => listener());
};

const handleStorageEvent = (event: StorageEvent) => {
  if (event.key !== null && event.key !== STORAGE_KEY) {
    return;
  }

  const password = readStoredPassword();
  if (password !== snapshot.password) {
    setSnapshot({ password, isRejected: snapshot.isRejected && password === null });
  }
};

export const getApiPasswordState = (): ApiPasswordState => snapshot;

export const getApiPassword = (): string => snapshot.password ?? '';

export const setApiPassword = (password: string) => {
  try {
    localStorage.setItem(STORAGE_KEY, password);
  } catch {
    // storage unavailable (private mode, blocked cookies) — keep it in memory for this session
  }

  setSnapshot({ password, isRejected: false });
};

export const clearApiPassword = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore: the in-memory snapshot below is the source of truth for the UI
  }

  setSnapshot({ password: null, isRejected: false });
};

/** Drops a password the backend refused, so the gate asks for a new one. */
export const rejectApiPassword = () => {
  if (snapshot.password === null && snapshot.isRejected) {
    return;
  }

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore: see clearApiPassword
  }

  setSnapshot({ password: null, isRejected: true });
};

export const subscribeToApiPassword = (listener: Listener) => {
  if (listeners.size === 0) {
    window.addEventListener('storage', handleStorageEvent);
  }
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.removeEventListener('storage', handleStorageEvent);
    }
  };
};
